import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface DbProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  original_price: number | null;
  category: string | null;
  images: string[] | null;
  stock: number;
  sales: number;
  status: string;
}

interface RelatedProduct {
  id: string;
  name: string;
  price: number;
  images: string[] | null;
}

const StarRating = ({ value }: { value: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <i key={s} className={`text-xs ${s <= Math.round(value) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`} />
    ))}
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState<DbProduct | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [userPoints, setUserPoints] = useState<number>(0);

  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'detail' | 'reviews' | 'recommend'>('detail');
  const [cartCount, setCartCount] = useState(0);
  const [showSpecSheet, setShowSpecSheet] = useState(false);
  const [sheetAction, setSheetAction] = useState<'cart' | 'buy'>('cart');
  const [addedTip, setAddedTip] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [cartBtnAnim, setCartBtnAnim] = useState(false);
  const [sheetCartBtnAnim, setSheetCartBtnAnim] = useState(false);
  const [heartAnim, setHeartAnim] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  // 加载商品数据
  useEffect(() => {
    if (!id) return;
    setLoadingProduct(true);

    const loadProduct = () => {
      supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .maybeSingle()
        .then(({ data }) => {
          setProduct((data as DbProduct | null));
          setLoadingProduct(false);
        });
    };

    loadProduct();

    // 页面重新可见时（从后台切回来）立刻刷新数据，确保看到最新内容
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setProduct(data as DbProduct);
          });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id]);

  // 加载相关商品
  useEffect(() => {
    if (!product?.category) return;
    supabase
      .from('products')
      .select('id, name, price, images')
      .eq('category', product.category)
      .eq('status', '上架')
      .neq('id', product.id)
      .limit(4)
      .then(({ data }) => {
        if (data) setRelatedProducts(data as RelatedProduct[]);
      });
  }, [product?.category, product?.id]);

  // 直接从DB读取最新积分
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('custom_users')
      .select('points')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setUserPoints(data.points);
      });
  }, [user?.id]);

  const openSheet = (action: 'cart' | 'buy') => {
    setSheetAction(action);
    setShowSpecSheet(true);
  };

  const writeToCart = useCallback(async (sel: boolean) => {
    if (!user || !product) {
      setShowSpecSheet(false);
      navigate('/login');
      return false;
    }
    const { data: existing } = await supabase
      .from('cart_items')
      .select('id, quantity')
      .eq('user_id', user.id)
      .eq('product_id', product.id)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('cart_items')
        .update({ quantity: existing.quantity + quantity, selected: sel })
        .eq('id', existing.id);
    } else {
      await supabase.from('cart_items').insert({
        user_id: user.id,
        product_id: product.id,
        quantity,
        selected: sel,
      });
    }
    return true;
  }, [user, product, quantity, navigate]);

  const handleConfirm = async () => {
    if (sheetAction === 'cart') {
      // 加入兑换车：写入购物车，selected=true
      setSheetCartBtnAnim(true);
      setTimeout(() => setSheetCartBtnAnim(false), 500);
      const ok = await writeToCart(true);
      if (!ok) return;
      setShowSpecSheet(false);
      setCartCount((p) => p + quantity);
      setAddedTip('已加入兑换车');
      setTimeout(() => setAddedTip(''), 1800);
    } else {
      // 立即兑换：先把所有购物车商品取消选中，再把本商品写入购物车并选中，然后跳转结算
      if (!user) { setShowSpecSheet(false); navigate('/login'); return; }
      if (!canExchange) return;
      setSheetCartBtnAnim(true);
      setTimeout(() => setSheetCartBtnAnim(false), 300);
      // 1. 取消所有已选中
      await supabase.from('cart_items').update({ selected: false }).eq('user_id', user.id);
      // 2. 写入本商品（selected=true）
      const ok = await writeToCart(true);
      if (!ok) return;
      setShowSpecSheet(false);
      navigate('/checkout');
    }
  };

  const handleAddToCart = () => {
    if (!user) { navigate('/login'); return; }
    setCartBtnAnim(true);
    setTimeout(() => setCartBtnAnim(false), 500);
    openSheet('cart');
  };

  if (loadingProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 max-w-[480px] mx-auto">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 max-w-[480px] mx-auto">
        <i className="ri-error-warning-line text-5xl text-gray-300 mb-4" />
        <p className="text-gray-500 mb-6">商品不存在或已下架</p>
        <button onClick={() => navigate('/shop')} className="bg-emerald-500 text-white px-8 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap">
          去逛逛
        </button>
      </div>
    );
  }

  const totalPoints = Number(product.price) * quantity;
  const canExchange = userPoints >= totalPoints;
  const images = product.images && product.images.length > 0
    ? product.images
    : [];

  const tabs = [
    { key: 'detail' as const, label: '商品详情' },
    { key: 'reviews' as const, label: '评价(0)' },
    { key: 'recommend' as const, label: '推荐' },
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50 max-w-[480px] mx-auto pb-24">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="flex items-center justify-between px-4 h-14">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-arrow-left-line text-gray-700 text-xl" />
            </button>
            <span className="font-semibold text-gray-900 text-sm">商品详情</span>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/cart')} className="w-8 h-8 flex items-center justify-center relative cursor-pointer">
                <i className="ri-shopping-cart-line text-gray-600 text-xl" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-emerald-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">{cartCount}</span>
                )}
              </button>
              <button className="w-8 h-8 flex items-center justify-center cursor-pointer">
                <i className="ri-share-line text-gray-600 text-xl" />
              </button>
            </div>
          </div>
        </header>

        {/* Image Gallery */}
        <div className="w-full bg-gray-100 overflow-hidden" style={{ height: 320 }}>
          <img src={images[0]} alt={product.name} className="w-full h-full object-cover object-top" />
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-hide bg-white">
            {images.map((img, i) => (
              <div key={i} className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent">
                <img src={img} alt={`${i}`} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
            ))}
          </div>
        )}

        {/* Price & Title */}
        <LazySection animation="fadeUp" delay={0}>
          <div className="bg-white px-4 pt-4 pb-3">
            <h1 className="text-base font-bold text-gray-900 leading-snug mb-3">{product.name}</h1>
            <div className="flex items-end gap-2 mb-3 flex-wrap">
              <span className="text-emerald-600 font-bold text-2xl">{Number(product.price).toLocaleString()}</span>
              <span className="text-emerald-600 text-sm font-medium mb-0.5">积分</span>
              {product.original_price && (
                <>
                  <span className="text-gray-400 text-sm line-through mb-0.5">{Number(product.original_price).toLocaleString()}积分</span>
                  <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded ml-1">
                    省{(Number(product.original_price) - Number(product.price)).toLocaleString()}积分
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400" />4.8 分</span>
              <span>已兑 {product.sales.toLocaleString()}</span>
              <span>库存 {product.stock} 件</span>
            </div>
            <div className="mt-3 flex items-center justify-between bg-emerald-50 rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5">
                <i className="ri-copper-coin-line text-emerald-600 text-sm" />
                <span className="text-xs text-gray-600">我的积分：<span className="text-emerald-600 font-bold">{user ? userPoints.toLocaleString() : '—'}</span></span>
              </div>
              {!user ? (
                <button
                  onClick={() => navigate('/login')}
                  className="text-xs font-medium text-emerald-600 underline cursor-pointer whitespace-nowrap"
                >
                  请先登录
                </button>
              ) : (
                <span className={`text-xs font-medium ${canExchange ? 'text-emerald-600' : 'text-rose-500'}`}>
                  {canExchange ? '积分充足，可兑换' : `积分不足，还差${(totalPoints - userPoints).toLocaleString()}`}
                </span>
              )}
            </div>
          </div>
        </LazySection>

        {/* Spec Preview */}
        <LazySection animation="fadeUp" delay={60}>
          <button onClick={() => openSheet('cart')} className="w-full bg-white mt-2 px-4 py-3.5 flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">规格：</span>
              <span className="text-sm text-gray-800 font-medium">默认规格</span>
            </div>
            <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
          </button>
        </LazySection>

        {/* Service */}
        <LazySection animation="fadeUp" delay={100}>
          <div className="bg-white mt-2 px-4 py-3 flex items-center gap-5 flex-wrap">
            {[
              { icon: 'ri-shield-check-line', text: '正品保障' },
              { icon: 'ri-truck-line', text: '极速发货' },
              { icon: 'ri-leaf-line', text: '纯植物配方' },
            ].map((s) => (
              <div key={s.text} className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className={`${s.icon} text-emerald-500`} />{s.text}
              </div>
            ))}
          </div>
        </LazySection>



        {/* Tabs */}
        <div className="bg-white mt-2 sticky top-14 z-30">
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-500'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white px-4 py-4">
          {activeTab === 'detail' && (
            <div>
              <LazySection animation="fadeUp" delay={0}>
                {product.description ? (
                  <div
                    className="text-sm text-gray-600 leading-relaxed rich-content"
                    dangerouslySetInnerHTML={{
                      __html: product.description.startsWith('<')
                        ? product.description
                        : product.description
                            .split('\n')
                            .map((line) => line.trim())
                            .filter(Boolean)
                            .map((line) => `<p>${line}</p>`)
                            .join(''),
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500">暂无详细描述</p>
                )}
              </LazySection>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-chat-3-line text-4xl block mb-2" />
              <p className="text-sm">暂无评价</p>
            </div>
          )}

          {activeTab === 'recommend' && (
            <div>
              <p className="text-sm text-gray-500 mb-3">同类好物推荐</p>
              {relatedProducts.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {relatedProducts.map((p, idx) => (
                    <LazySection key={p.id} animation="fadeUp" delay={idx * 80}>
                      <div
                        onClick={() => navigate(`/product/${p.id}`)}
                        className="bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer active:scale-95 transition-transform"
                      >
                        <div className="w-full aspect-square bg-gray-50">
                          <img
                            src={p.images?.[0] ?? ''}
                            alt={p.name}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-3">
                          <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug mb-1.5">{p.name}</p>
                          <span className="text-emerald-600 font-bold text-base">{Number(p.price).toLocaleString()}</span>
                          <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                        </div>
                      </div>
                    </LazySection>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 text-gray-400 text-sm">暂无同类商品</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          backgroundColor: '#ffffff', borderTop: '1px solid #f3f4f6',
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          maxWidth: '480px', margin: '0 auto',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setHeartAnim(true); setTimeout(() => setHeartAnim(false), 500); setIsFavorited(!isFavorited); }}
            className="flex flex-col items-center gap-0.5 cursor-pointer w-10"
          >
            <i className={`text-xl ${isFavorited ? 'ri-heart-fill text-rose-500' : 'ri-heart-line text-gray-500'} ${heartAnim ? 'scale-125' : ''} transition-transform`} />
            <span className="text-xs text-gray-500">收藏</span>
          </button>
          <button onClick={() => navigate('/cart')} className="flex flex-col items-center gap-0.5 cursor-pointer w-10">
            <i className="ri-shopping-cart-line text-gray-500 text-xl" />
            <span className="text-xs text-gray-500">兑换车</span>
          </button>
          <button
            onClick={handleAddToCart}
            className={`flex-1 bg-amber-400 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-transform duration-150 ${cartBtnAnim ? 'scale-90' : 'scale-100'}`}
          >
            加入兑换车
          </button>
          <button
            onClick={() => { if (!user) { navigate('/login'); return; } openSheet('buy'); }}
            className={`flex-1 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${!user || canExchange ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`}
          >
            立即兑换
          </button>
        </div>
      </div>

      {/* Spec Bottom Sheet */}
      {showSpecSheet && (
        <>
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10000 }} onClick={() => setShowSpecSheet(false)} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: '480px', margin: '0 auto', backgroundColor: '#fff', borderRadius: '16px 16px 0 0', zIndex: 10001 }}>
            <div className="px-4 pt-4 pb-24 overflow-y-auto max-h-[85vh]">
              <div className="flex items-start gap-4 mb-5 pb-4 border-b border-gray-100">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                  <img src={images[0]} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-emerald-600 font-bold text-xl">{Number(product.price).toLocaleString()}</span>
                    <span className="text-emerald-600 text-sm">积分</span>
                  </div>
                  {product.original_price && (
                    <p className="text-xs text-gray-400 line-through">{Number(product.original_price).toLocaleString()}积分</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">库存 {product.stock} 件</p>
                </div>
                <button onClick={() => setShowSpecSheet(false)} className="cursor-pointer">
                  <i className="ri-close-line text-gray-400 text-xl" />
                </button>
              </div>

              <div className="flex items-center justify-between mt-5">
                <span className="text-sm text-gray-700 font-medium">兑换数量</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQuantity((p) => Math.max(1, p - 1))} className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full cursor-pointer">
                    <i className="ri-subtract-line text-gray-600 text-sm" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={product.stock}
                    value={quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (isNaN(val) || val < 1) { setQuantity(1); return; }
                      setQuantity(Math.min(product.stock, val));
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (isNaN(val) || val < 1) setQuantity(1);
                    }}
                    className="text-base font-semibold text-center border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400"
                    style={{ width: 56, height: 32 }}
                  />
                  <button onClick={() => setQuantity((p) => Math.min(product.stock, p + 1))} className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full cursor-pointer">
                    <i className="ri-add-line text-gray-600 text-sm" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">共消耗积分：</span>
                <span className={`font-bold text-base ${canExchange ? 'text-emerald-600' : 'text-rose-500'}`}>{totalPoints.toLocaleString()} 积分</span>
              </div>

              <button
                onClick={handleConfirm}
                disabled={sheetAction === 'buy' && !canExchange}
                className={`w-full mt-4 py-3.5 rounded-full text-white font-medium text-sm cursor-pointer whitespace-nowrap transition-transform duration-150 ${
                  sheetAction === 'cart' ? 'bg-amber-400' : canExchange ? 'bg-emerald-500' : 'bg-gray-300'
                } ${sheetCartBtnAnim ? 'scale-90' : 'scale-100'}`}
              >
                {sheetAction === 'cart' ? '加入兑换车' : canExchange ? '立即兑换' : '积分不足'}
              </button>
            </div>
          </div>
        </>
      )}

      {addedTip && (
        <div style={{ position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 10002 }}
          className="bg-gray-800/90 text-white text-sm px-4 py-2 rounded-full">
          {addedTip}
        </div>
      )}

      {/* 富文本内容样式 */}
      <style>{`
        .rich-content {
          line-height: 1.8;
        }
        .rich-content p {
          margin-bottom: 0.75rem;
        }
        .rich-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
          display: block;
        }
        .rich-content ul, .rich-content ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .rich-content ul { list-style-type: disc; }
        .rich-content ol { list-style-type: decimal; }
        .rich-content li {
          margin-bottom: 0.25rem;
        }
        .rich-content h1, .rich-content h2, .rich-content h3, .rich-content h4 {
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          color: #111827;
        }
        .rich-content h1 { font-size: 1.25rem; }
        .rich-content h2 { font-size: 1.125rem; }
        .rich-content h3 { font-size: 1rem; }
        .rich-content strong { font-weight: 600; color: #111827; }
        .rich-content em { font-style: italic; }
        .rich-content u { text-decoration: underline; }
        .rich-content s { text-decoration: line-through; }
        .rich-content a { color: #059669; text-decoration: underline; }
        .rich-content blockquote {
          border-left: 3px solid #d1fae5;
          padding-left: 0.75rem;
          margin: 0.75rem 0;
          color: #6b7280;
        }
      `}</style>
    </>
  );
};

export default ProductDetailPage;
